import { api } from "@/api"

import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"

export type ImportProduct = {
  id: string | null
  parentCategory: string | null
  name: string
  description: string
  hasVariablePrice: boolean
  price: number
  vatRate: number | null
  selfColor: string | null
}

type ImportRowError = {
  number: number
  data: object
  errors: {
    msg: string
    loc: string[]
    type: string
  }[]
}

export type ProductImportRow =
  | {
      number: number
      status: "new"
      oldInstance: null
      patch: ImportProduct
    }
  | {
      number: number
      status: "update"
      oldInstance: ImportProduct
      patch: Partial<ImportProduct>
    }
  | {
      number: number
      status: "unchanged"
      oldInstance: ImportProduct
      patch: Record<string, never>
    }
  | {
      number: number
      status: "delete"
      oldInstance: ImportProduct
      patch: null
    }

type RetrieveImportJobResponse =
  | {
      id: string
      data: {
        rows: ProductImportRow[]
        rowErrors?: ImportRowError[]
      }
      status: "dr_finished" | "pending" | "started"

      createdAt: Date
      rowErrors?: boolean
      importedAt: null
    }
  | {
      id: string
      data: {
        rows: ProductImportRow[]
      }
      status: "finished"
      createdAt: Date
      rowErrors?: boolean
      importedAt: Date
    }
  | {
      id: string
      data: null
      status: "dr_pending" | "dr_started"
      createdAt: Date
      rowErrors?: boolean
      importedAt: null
    }

const parseResponse = (
  importResponse: RecursivelyConvertDatesToStrings<RetrieveImportJobResponse>
): RetrieveImportJobResponse =>
  ({
    ...importResponse,
    createdAt: DateFns.parseISO(importResponse.createdAt),
    importedAt: importResponse.importedAt
      ? DateFns.parseISO(importResponse.importedAt)
      : null,
  }) as RetrieveImportJobResponse

const retrieveImportJob = async (importId: string) => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<RetrieveImportJobResponse>
  >(`/imports/${importId}/`)
  return parseResponse(body)
}

type RetrieveLatestImportJobResponse = {
  latestJob: RetrieveImportJobResponse | null
}

const retrieveLatestImport = async () => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<RetrieveLatestImportJobResponse>
  >(`/imports/latest/`)

  return body.latestJob
    ? parseResponse(body.latestJob)
    : null
}

export const useImportJob = (importId: MaybeRef<string>) =>
  useQuery({
    queryKey: [BASE_PRODUCT_IMPORT_QUERY_KEY, importId],
    queryFn: async () => {
      const importResponse = await retrieveImportJob(
        unref(importId)
      )
      return importResponse
    },
    refetchInterval: 5 * 1000,
  })

export const useLatestImport = () =>
  useQuery({
    queryKey: [BASE_PRODUCT_IMPORT_QUERY_KEY, "latest"],
    queryFn: async () => {
      const importResponse = await retrieveLatestImport()
      return importResponse
    },
    refetchInterval: 5 * 1000,
  })
